import React from 'react';
import CaliperSiteFigure, { CALIPER_SITE_KEYS } from '../components/CaliperSiteFigure.jsx';

/**
 * Minimal markdown-ish renderer used by BlogSection cards (excerpt preview)
 * and the full BlogPostPage. Supports:
 *   ## h2  |  ### h3  |  **bold**  |  • / - / * bullet lists  |  paragraphs
 *   [[illustration:<key>]]  — inline anatomical figure (see CaliperSiteFigure).
 */
export const renderArticleContent = (content, options = {}) => {
  if (!content) return null;
  const { onBooking } = options;
  const blocks = content.split(/\n\n+/);

  const renderInline = (text) =>
    text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g).map((part, i) => {
      const bold = part.match(/^\*\*(.+)\*\*$/);
      if (bold) return <strong key={i} className="text-gray-900">{bold[1]}</strong>;
      const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      if (link) {
        const [, label, href] = link;
        // #booking — a cikk végi felhívás az időpontfoglaló ablakot nyitja meg
        if (href === '#booking') {
          return (
            <button
              key={i}
              type="button"
              data-testid="article-booking-cta"
              onClick={() => (onBooking ? onBooking() : window.location.assign('/#contact'))}
              className="inline-flex items-center gap-1 rounded-full bg-yellow-600 hover:bg-yellow-500 px-5 py-2.5 text-sm md:text-base font-bold text-gray-900 transition-colors"
            >
              {label}
            </button>
          );
        }
        const external = /^https?:\/\//i.test(href);
        return (
          <a
            key={i}
            href={href}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="text-yellow-700 font-semibold underline underline-offset-2 hover:text-yellow-600 transition-colors"
          >
            {label}
          </a>
        );
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });

  const isBulletLine = (line) => /^\s*[•\-*]\s+/.test(line);
  const stripBullet = (line) => line.replace(/^\s*[•\-*]\s+/, '');

  return blocks.map((block, idx) => {
    const trimmed = block.trim();

    // ![alt](url) — inline article image. Whole-block figure rendered as a
    // centered image with optional caption (alt text). Must be its own block
    // (surrounded by blank lines) so it doesn't leak into paragraphs.
    const imgMatch = trimmed.match(/^!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)$/);
    if (imgMatch) {
      const [, alt, src] = imgMatch;
      return (
        <figure key={idx} className="my-8">
          <img
            src={src}
            alt={alt || ''}
            loading="lazy"
            className="w-full h-auto rounded-xl shadow-md border border-gray-100"
          />
          {alt && (
            <figcaption className="mt-2 text-center text-xs md:text-sm text-gray-500 italic">
              {alt}
            </figcaption>
          )}
        </figure>
      );
    }

    // [[illustration:key]] — render an anatomical figure (currently only
    // caliper measurement sites; unknown keys are skipped silently).
    const illMatch = trimmed.match(/^\[\[illustration:([a-z0-9_-]+)\]\]$/i);
    if (illMatch) {
      const key = illMatch[1].toLowerCase();
      if (CALIPER_SITE_KEYS.includes(key)) {
        return <CaliperSiteFigure key={idx} site={key} />;
      }
      return null;
    }

    if (block.startsWith('## ')) {
      return (
        <h2 key={idx}
            className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4 leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}>
          {block.slice(3)}
        </h2>
      );
    }
    if (block.startsWith('### ')) {
      return (
        <h3 key={idx}
            className="text-lg md:text-xl font-bold text-gray-900 mt-7 mb-2 leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}>
          {block.slice(4)}
        </h3>
      );
    }

    const lines = block.split('\n').filter((l) => l.trim().length > 0);

    // Markdown táblázat: | a | b | fejléc + | --- | elválasztó sor
    if (lines.length >= 2 && lines[0].trim().startsWith('|') && /^\s*\|[\s:|-]+\|\s*$/.test(lines[1])) {
      const cells = (row) => row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
      const head = cells(lines[0]);
      const rows = lines.slice(2).map(cells);
      return (
        <div key={idx} className="my-7 -mx-6 px-6 overflow-x-auto md:mx-0 md:px-0">
          <table data-testid="article-table" className="w-full min-w-[520px] border-collapse text-sm md:text-base">
            <thead>
              <tr className="bg-gray-900 text-white">
                {head.map((h, i) => (
                  <th key={i} className="text-left font-bold px-4 py-3 first:rounded-tl-lg last:rounded-tr-lg">
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={i % 2 ? 'bg-gray-50' : 'bg-white'}>
                  {r.map((c, j) => (
                    <td
                      key={j}
                      className={`align-top px-4 py-3 border-b border-gray-200 text-gray-700 ${j === 0 ? 'font-semibold text-gray-900' : ''}`}
                    >
                      {renderInline(c)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (lines.length > 0 && lines.every(isBulletLine)) {
      return (
        <ul key={idx} className="list-none pl-1 mb-5 space-y-2">
          {lines.map((line, i) => (
            <li key={i} className="flex gap-3 text-gray-700 leading-relaxed text-base md:text-lg">
              <span className="text-amber-600 font-bold mt-[2px] select-none" aria-hidden="true">•</span>
              <span className="flex-1">{renderInline(stripBullet(line))}</span>
            </li>
          ))}
        </ul>
      );
    }

    return (
      <p key={idx} className="text-gray-700 leading-relaxed mb-4 text-base md:text-lg">
        {block.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {renderInline(line)}
          </React.Fragment>
        ))}
      </p>
    );
  });
};
